import type { QueryClient } from '@tanstack/react-query';
import type { AssetCard } from '@/lib/api/types';
import type { PlaceholderCard, PlaceholderStatus, BoardCardEntry } from '@/features/grid/types';
import { isPlaceholder } from '@/features/grid/types';
import type { UploadRecord } from '@/stores/upload';
import { assetTypeOf } from './sniff';

/** Minimal shape of the infinite board-assets cache this module patches (keeps it test-friendly). */
export type Page = { items: BoardCardEntry[]; total?: number | null; next_cursor?: string | null };
export type Infinite = { pages: Page[]; pageParams: unknown[] };

export function placeholderFromRecord(r: UploadRecord): PlaceholderCard {
  const status: PlaceholderStatus = r.status === 'paused' ? 'paused' : r.status;
  const aspect = r.preview && r.preview.width && r.preview.height ? r.preview.width / r.preview.height : 1;
  const progress = r.status === 'hashing' ? (r.hash_progress ?? 0) : r.status === 'uploading' || r.status === 'paused' ? (r.size ? r.bytesSent / r.size : 0) : r.status === 'processing' ? (r.processing_progress ?? 0) / 100 : r.status === 'completing' || r.status === 'ready' ? 1 : 0;
  return {
    __placeholder: true,
    id: r.client_upload_id,
    client_upload_id: r.client_upload_id,
    title: r.name,
    aspect_ratio: aspect,
    preview_url: r.preview?.blobUrl || null,
    status: r.duplicateOf && status === 'processing' ? 'duplicate' : status,
    progress: Math.max(0, Math.min(1, progress)),
    error: r.error?.message ?? null,
    retryable: r.error?.retryable ?? false,
    type: assetTypeOf(r.detected_mime),
    size_bytes: r.size,
    asset_id: r.asset_id ?? null,
    updated_at: new Date().toISOString(),
  };
}

const boardKey = (boardId: string) => ['board-assets', boardId];

function mapFirstPage(old: Infinite | undefined, fn: (items: BoardCardEntry[], page: Page) => { items: BoardCardEntry[]; totalDelta?: number } | null): Infinite | undefined {
  if (!old || old.pages.length === 0) return old;
  const first = old.pages[0]!;
  const r = fn(first.items, first);
  if (!r) return old;
  const pages = old.pages.slice();
  pages[0] = { ...first, items: r.items, total: first.total == null ? first.total : first.total + (r.totalDelta ?? 0) };
  return { ...old, pages };
}

/** §3.6 — insert at the top of the first page of every matching board query (`total += 1`). */
export function insertPlaceholder(qc: QueryClient, boardId: string, card: PlaceholderCard): void {
  qc.setQueriesData<Infinite>({ queryKey: boardKey(boardId) }, (old) =>
    mapFirstPage(old, (items) => (items.some((c) => c.id === card.id) ? null : { items: [card, ...items], totalDelta: 1 })),
  );
}

/** Update the placeholder object in place (same key → grid keeps the slot; height animates on aspect change). */
export function updatePlaceholder(qc: QueryClient, boardId: string, card: PlaceholderCard): void {
  qc.setQueriesData<Infinite>({ queryKey: boardKey(boardId) }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((p) => {
      const i = p.items.findIndex((c) => c.id === card.id);
      if (i < 0) return p;
      touched = true;
      const items = p.items.slice();
      items[i] = card;
      return { ...p, items };
    });
    return touched ? { ...old, pages } : old;
  });
}

/**
 * Replace the placeholder with the real AssetCard in place. The grid item key stays the placeholder
 * id (`client_upload_id`) so the slot is reused; we keep a shadow `id` on the card for that.
 */
export function replacePlaceholder(qc: QueryClient, boardId: string, clientUploadId: string, card: AssetCard): boolean {
  let replaced = false;
  qc.setQueriesData<Infinite>({ queryKey: boardKey(boardId) }, (old) => {
    if (!old) return old;
    let touched = false;
    const pages = old.pages.map((p) => {
      const i = p.items.findIndex((c) => c.id === clientUploadId || c.id === card.id);
      if (i < 0) return p;
      touched = true;
      const items = p.items.slice();
      // drop any duplicate copy of the real card delivered by asset.created before the swap
      const dupe = items.findIndex((c, j) => j !== i && c.id === card.id);
      if (dupe >= 0) items.splice(dupe, 1);
      items[i] = card;
      return { ...p, items };
    });
    if (touched) replaced = true;
    return touched ? { ...old, pages } : old;
  });
  return replaced;
}

export function removePlaceholder(qc: QueryClient, boardId: string, id: string): void {
  qc.setQueriesData<Infinite>({ queryKey: boardKey(boardId) }, (old) =>
    mapFirstPage(old, (items) => {
      const i = items.findIndex((c) => c.id === id);
      if (i < 0) return null;
      const next = items.slice();
      next.splice(i, 1);
      return { items: next, totalDelta: -1 };
    }),
  );
}

/** True when the cache already holds a card for this asset (placeholder swapped or real). */
export function boardHasAsset(qc: QueryClient, boardId: string, assetId: string): boolean {
  const all = qc.getQueriesData<Infinite>({ queryKey: boardKey(boardId) });
  return all.some(([, d]) => d?.pages.some((p) => p.items.some((c) => c.id === assetId || (isPlaceholder(c) && c.asset_id === assetId))));
}

/**
 * §3.6 refetch merge — re-inject placeholders not yet reflected in fresh server data.
 * `live` = placeholders for this board still in the store (any non-dismissed status).
 */
export function mergePlaceholders(items: BoardCardEntry[], live: readonly PlaceholderCard[], isFirstPage: boolean): BoardCardEntry[] {
  if (!isFirstPage || live.length === 0) return items;
  const present = new Set<string>();
  for (const c of items) present.add(c.id);
  const missing = live.filter((p) => !present.has(p.id) && !(p.asset_id && present.has(p.asset_id)));
  return missing.length ? [...missing, ...items] : items;
}
